// ════════════════════════════════════════════════════════════
// /tresorerie — soldes des comptes + mouvements + transferts
// ════════════════════════════════════════════════════════════
// - Cards soldes : solde initial + encaissements reçus − dépenses payées
//   ± transferts (vue SQL tresorerie_soldes). Clic = filtre les mouvements.
// - Onglet Mouvements : historique unifié (encaissements / dépenses /
//   transferts) par compte.
// - Onglet Transferts : transfert de fonds en une étape (tous les rôles),
//   annulation DAF only.
// - Soldes initiaux : édition DAF only (donnés par Sidi au lancement).
// ════════════════════════════════════════════════════════════
import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowRightLeft,
  Landmark,
  Loader2,
  Pencil,
  X,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  useSoldesTresorerie,
  useMouvementsTresorerie,
  useTransferts,
  useCreateTransfert,
  useAnnulerTransfert,
  useUpdateSoldeInitial,
  type SoldeCompte,
  type MouvementTresorerie,
  type Transfert,
} from '@/queries/tresorerie';
import { useAuth } from '@/hooks/useAuth';
import { CompteLogo } from '@/components/tresorerie/CompteLogo';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { MoneyInput } from '@/components/ui/MoneyInput';
import { Label } from '@/components/ui/Label';
import { cn } from '@/lib/utils';
import { getPaiementSignedUrl } from '@/lib/uploadStorage';
import { formatMRU } from '@ecr/core/utils';

export const Route = createFileRoute('/_authed/tresorerie/')({
  staticData: { crumb: 'Trésorerie' },
  component: TresoreriePage,
});

const SOURCE_LABELS: Record<MouvementTresorerie['source'], string> = {
  encaissement: 'Encaissement',
  depense: 'Dépense',
  transfert: 'Transfert',
};

function todayYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function TresoreriePage() {
  const { profile } = useAuth();
  const isDaf = profile?.role === 'daf';

  const [compteFilter, setCompteFilter] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'mouvements' | 'transferts'>('mouvements');
  const [showTransfertModal, setShowTransfertModal] = useState(false);
  const [showSoldesModal, setShowSoldesModal] = useState(false);

  const { data: soldes = [], isLoading: loadingSoldes } = useSoldesTresorerie();
  const { data: mouvements = [], isLoading: loadingMouvements } =
    useMouvementsTresorerie(compteFilter);
  const { data: transferts = [] } = useTransferts();

  const soldeTotal = soldes.reduce((acc, s) => acc + s.solde, 0);
  const compteById = new Map(soldes.map((s) => [s.id, s]));

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-foreground text-xl font-semibold tracking-tight sm:text-2xl">
              Trésorerie
            </h1>
            <p className="text-muted-foreground text-sm">
              Total : <span className="font-mono font-semibold">{formatMRU(soldeTotal)}</span>
            </p>
          </div>
          <div className="flex shrink-0 gap-1.5">
            {isDaf && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => setShowSoldesModal(true)}
              >
                <Pencil className="size-4" />
                <span className="hidden sm:inline">Soldes initiaux</span>
              </Button>
            )}
            <Button size="sm" className="gap-1.5" onClick={() => setShowTransfertModal(true)}>
              <ArrowRightLeft className="size-4" />
              <span className="hidden sm:inline">Nouveau transfert</span>
              <span className="sm:hidden">Transfert</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Soldes des comptes */}
      {loadingSoldes ? (
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div key={i} className="bg-muted h-24 animate-pulse rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
          {soldes.map((s) => (
            <CompteCard
              key={s.id}
              solde={s}
              active={compteFilter === s.id}
              onClick={() => {
                setCompteFilter(compteFilter === s.id ? null : s.id);
                setActiveTab('mouvements');
              }}
            />
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="bg-background border-border sticky top-12 z-20 -mx-4 overflow-x-auto border-b sm:top-14 sm:mx-0">
        <div className="flex min-w-max gap-1 px-4 sm:min-w-0 sm:px-0">
          {(
            [
              { key: 'mouvements', label: 'Mouvements' },
              { key: 'transferts', label: 'Transferts' },
            ] as const
          ).map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors',
                activeTab === tab.key
                  ? 'border-primary text-foreground'
                  : 'text-muted-foreground hover:text-foreground border-transparent',
              )}
            >
              {tab.label}
              {tab.key === 'transferts' && transferts.length > 0 && (
                <span className="text-muted-foreground ml-1.5 text-xs">({transferts.length})</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Contenu */}
      {activeTab === 'mouvements' ? (
        <MouvementsList
          mouvements={mouvements}
          isLoading={loadingMouvements}
          compteById={compteById}
          compteFilter={compteFilter}
        />
      ) : (
        <TransfertsList transferts={transferts} compteById={compteById} isDaf={isDaf} />
      )}

      {/* Modals */}
      {showTransfertModal && (
        <TransfertModal comptes={soldes} onClose={() => setShowTransfertModal(false)} />
      )}
      {showSoldesModal && (
        <SoldesInitiauxModal comptes={soldes} onClose={() => setShowSoldesModal(false)} />
      )}
    </div>
  );
}

// ─── Card compte ─────────────────────────────────────────
function CompteCard({
  solde,
  active,
  onClick,
}: {
  solde: SoldeCompte;
  active: boolean;
  onClick: () => void;
}) {
  const negatif = solde.solde < 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'bg-card flex flex-col gap-2 rounded-lg border p-3 text-left transition-all',
        'hover:bg-accent active:scale-[0.98]',
        active ? 'border-primary ring-primary/30 ring-2' : 'border-border',
      )}
    >
      <div className="flex items-center gap-2">
        <CompteLogo logoKey={solde.logoKey} type={solde.type} className="size-12" />
        <span className="text-foreground truncate text-xs font-medium">{solde.label}</span>
      </div>
      <p
        className={cn(
          'font-mono text-base font-bold tabular-nums leading-none sm:text-lg',
          negatif ? 'text-red-600 dark:text-red-400' : 'text-foreground',
        )}
      >
        {formatMRU(solde.solde)}
      </p>
      <p className="text-muted-foreground text-[10px] leading-tight">
        +{formatMRU(solde.totalCredits)} · −{formatMRU(solde.totalDebits)}
      </p>
    </button>
  );
}

// ─── Mouvements ──────────────────────────────────────────
function MouvementsList({
  mouvements,
  isLoading,
  compteById,
  compteFilter,
}: {
  mouvements: MouvementTresorerie[];
  isLoading: boolean;
  compteById: Map<string, SoldeCompte>;
  compteFilter: string | null;
}) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-muted h-14 animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }
  if (mouvements.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
          <Landmark className="text-muted-foreground size-10" />
          <p className="text-foreground font-medium">Aucun mouvement</p>
          <p className="text-muted-foreground text-sm">
            {compteFilter
              ? 'Aucun mouvement sur ce compte pour le moment.'
              : 'Les encaissements reçus, dépenses payées et transferts apparaîtront ici.'}
          </p>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="grid gap-2">
      {mouvements.map((m) => {
        const compte = compteById.get(m.compteId);
        const credit = m.sens === 'credit';
        const SourceIcon =
          m.source === 'encaissement'
            ? ArrowDownToLine
            : m.source === 'depense'
              ? ArrowUpFromLine
              : ArrowRightLeft;
        const inner = (
          <Card>
            <CardContent className="flex items-center gap-3 py-2.5">
              <span
                className={cn(
                  'flex size-8 shrink-0 items-center justify-center rounded-full',
                  credit
                    ? 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300'
                    : 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
                )}
              >
                <SourceIcon className="size-4" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-foreground truncate text-sm font-medium">{m.libelle}</p>
                <p className="text-muted-foreground text-xs">
                  {new Intl.DateTimeFormat('fr-FR', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  }).format(m.dateMvt)}
                  {' · '}
                  {SOURCE_LABELS[m.source]} <span className="font-mono">{m.sourceCode}</span>
                  {!compteFilter && compte ? ` · ${compte.label}` : ''}
                </p>
              </div>
              <p
                className={cn(
                  'shrink-0 font-mono text-sm font-semibold tabular-nums',
                  credit ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300',
                )}
              >
                {credit ? '+' : '−'}
                {formatMRU(m.montant)}
              </p>
            </CardContent>
          </Card>
        );
        const key = `${m.source}-${m.sourceId}-${m.compteId}-${m.sens}`;
        if (m.source === 'encaissement') {
          return (
            <Link key={key} to="/paiements/$id" params={{ id: m.sourceId }} className="block">
              {inner}
            </Link>
          );
        }
        if (m.source === 'depense') {
          return (
            <Link key={key} to="/depenses/$id" params={{ id: m.sourceId }} className="block">
              {inner}
            </Link>
          );
        }
        return <div key={key}>{inner}</div>;
      })}
    </div>
  );
}

// ─── Transferts ──────────────────────────────────────────
function TransfertsList({
  transferts,
  compteById,
  isDaf,
}: {
  transferts: Transfert[];
  compteById: Map<string, SoldeCompte>;
  isDaf: boolean;
}) {
  const [annulerId, setAnnulerId] = useState<string | null>(null);

  if (transferts.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
          <ArrowRightLeft className="text-muted-foreground size-10" />
          <p className="text-foreground font-medium">Aucun transfert</p>
          <p className="text-muted-foreground text-sm">
            Utilise « Nouveau transfert » pour déplacer des fonds entre les comptes (ex. Caisse
            Mauripe → BMCI MRU).
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-2">
      {transferts.map((t) => {
        const source = compteById.get(t.compteSourceId);
        const dest = compteById.get(t.compteDestId);
        const annule = t.statut === 'annule';
        return (
          <Card key={t.id} className={cn(annule && 'opacity-60')}>
            <CardContent className="space-y-1.5 py-3">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-foreground font-mono text-sm font-semibold">{t.code}</span>
                {annule ? (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                    Annulé
                  </span>
                ) : (
                  <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-950/50 dark:text-green-300">
                    Effectué
                  </span>
                )}
                {t.creeParMauripe && (
                  <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-950/50 dark:text-blue-300">
                    Écrit par Mauripe{t.refMauripe ? ` · ${t.refMauripe}` : ''}
                  </span>
                )}
                <span className="text-muted-foreground ml-auto text-xs">
                  {new Intl.DateTimeFormat('fr-FR', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  }).format(t.dateTransfert)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <p className="text-foreground min-w-0 truncate text-sm">
                  {source?.label ?? '?'} <ArrowRightLeft className="inline size-3.5" />{' '}
                  {dest?.label ?? '?'}
                </p>
                <p className="text-foreground shrink-0 font-mono text-base font-semibold tabular-nums">
                  {formatMRU(t.montant)}
                </p>
              </div>
              {t.motif && <p className="text-muted-foreground text-xs italic">{t.motif}</p>}
              {annule && t.annuleMotif && (
                <p className="text-xs text-red-700 dark:text-red-300">
                  Annulé : {t.annuleMotif}
                  {t.annuleParEmail ? ` (par ${t.annuleParEmail.split('@')[0]})` : ''}
                </p>
              )}
              <p className="text-muted-foreground text-[11px]">
                {t.creeParMauripe
                  ? 'Reversement de Mauripe : il se corrige chez Mauripe'
                  : `Saisi par ${t.createdByEmail.split('@')[0]}`}
              </p>
              {t.justificatifPath && (
                <button
                  type="button"
                  className="text-primary text-xs font-medium underline-offset-2 hover:underline"
                  onClick={() => {
                    const path = t.justificatifPath;
                    if (!path) return;
                    getPaiementSignedUrl(path)
                      .then((url) => window.open(url, '_blank', 'noopener'))
                      .catch((e: unknown) =>
                        toast.error(e instanceof Error ? e.message : 'Capture introuvable'),
                      );
                  }}
                >
                  Voir la capture du virement
                </button>
              )}
              {isDaf && !annule && !t.creeParMauripe && (
                <div className="pt-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5 text-red-600 hover:text-red-700"
                    onClick={() => setAnnulerId(t.id)}
                  >
                    <XCircle className="size-4" />
                    Annuler
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {annulerId && (
        <AnnulerTransfertModal transfertId={annulerId} onClose={() => setAnnulerId(null)} />
      )}
    </div>
  );
}

// ─── Modal nouveau transfert ─────────────────────────────
function TransfertModal({ comptes, onClose }: { comptes: SoldeCompte[]; onClose: () => void }) {
  const createTransfert = useCreateTransfert();
  const [sourceId, setSourceId] = useState('');
  const [destId, setDestId] = useState('');
  const [montant, setMontant] = useState<number | null>(null);
  const [date, setDate] = useState(todayYmd());
  const [motif, setMotif] = useState('');

  const source = comptes.find((c) => c.id === sourceId);

  const onSubmit = async () => {
    if (!sourceId || !destId) {
      toast.error('Choisis le compte source et le compte destination');
      return;
    }
    if (sourceId === destId) {
      toast.error('Les deux comptes doivent être différents');
      return;
    }
    if (!montant || montant <= 0) {
      toast.error('Montant strictement positif requis');
      return;
    }
    try {
      const t = await createTransfert.mutateAsync({
        compteSourceId: sourceId,
        compteDestId: destId,
        montant,
        dateTransfert: date,
        motif: motif.trim() || null,
      });
      toast.success(`Transfert ${t.code} enregistré`);
      onClose();
    } catch (e) {
      toast.error(`Erreur : ${e instanceof Error ? e.message : 'inconnu'}`);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-card w-full max-w-md space-y-4 rounded-t-xl p-5 sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-foreground text-lg font-semibold">Transfert de fonds</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Fermer"
          >
            <X className="size-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="after:text-red-500 after:content-['*']">Compte source</Label>
            <select
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value)}
              className="border-input bg-background flex h-10 w-full rounded-md border px-3 text-sm"
            >
              <option value="">— Choisir —</option>
              {comptes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label} ({formatMRU(c.solde)})
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="after:text-red-500 after:content-['*']">Compte destination</Label>
            <select
              value={destId}
              onChange={(e) => setDestId(e.target.value)}
              className="border-input bg-background flex h-10 w-full rounded-md border px-3 text-sm"
            >
              <option value="">— Choisir —</option>
              {comptes
                .filter((c) => c.id !== sourceId)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="after:text-red-500 after:content-['*']">Montant (MRU)</Label>
              <MoneyInput value={montant} onChange={(v) => setMontant(v)} />
            </div>
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Motif (optionnel)</Label>
            <Input
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              placeholder="ex. Versement des espèces de la semaine"
            />
          </div>
          {source && montant != null && montant > source.solde && (
            <p className="rounded bg-orange-50 p-2 text-xs text-orange-800 dark:bg-orange-950/40 dark:text-orange-200">
              ⚠️ Le montant dépasse le solde actuel de {source.label} ({formatMRU(source.solde)}).
              Le transfert reste possible (solde initial pas encore saisi ?).
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button
            type="button"
            disabled={createTransfert.isPending}
            onClick={() => void onSubmit()}
            className="gap-2"
          >
            {createTransfert.isPending && <Loader2 className="size-4 animate-spin" />}
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal annulation transfert (DAF) ────────────────────
function AnnulerTransfertModal({
  transfertId,
  onClose,
}: {
  transfertId: string;
  onClose: () => void;
}) {
  const annuler = useAnnulerTransfert();
  const [motif, setMotif] = useState('');

  const onSubmit = async () => {
    if (!motif.trim()) {
      toast.error("Motif d'annulation obligatoire");
      return;
    }
    try {
      await annuler.mutateAsync({ id: transfertId, motif: motif.trim() });
      toast.success('Transfert annulé — les soldes sont recalculés');
      onClose();
    } catch (e) {
      toast.error(`Erreur : ${e instanceof Error ? e.message : 'inconnu'}`);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-card w-full max-w-md space-y-4 rounded-t-xl p-5 sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-foreground text-lg font-semibold">Annuler le transfert</h2>
        <p className="text-muted-foreground text-sm">
          Le transfert sera marqué annulé et retiré des soldes des deux comptes.
        </p>
        <div className="space-y-1.5">
          <Label className="after:text-red-500 after:content-['*']">Motif</Label>
          <textarea
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            rows={2}
            className="border-input bg-background flex w-full rounded-md border px-3 py-2 text-sm"
            placeholder="ex. Erreur de saisie"
            autoFocus
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Fermer
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={annuler.isPending}
            onClick={() => void onSubmit()}
            className="gap-2"
          >
            {annuler.isPending && <Loader2 className="size-4 animate-spin" />}
            Confirmer l&apos;annulation
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal soldes initiaux (DAF) ─────────────────────────
function SoldesInitiauxModal({
  comptes,
  onClose,
}: {
  comptes: SoldeCompte[];
  onClose: () => void;
}) {
  const updateSolde = useUpdateSoldeInitial();
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(comptes.map((c) => [c.id, String(c.soldeInitial)])),
  );
  const [saving, setSaving] = useState(false);

  const onSubmit = async () => {
    setSaving(true);
    try {
      for (const c of comptes) {
        const raw = (values[c.id] ?? '').replace(/\s/g, '').replace(',', '.');
        const parsed = raw === '' ? 0 : Number(raw);
        if (!isFinite(parsed)) {
          toast.error(`Solde invalide pour ${c.label}`);
          setSaving(false);
          return;
        }
        if (parsed !== c.soldeInitial) {
          await updateSolde.mutateAsync({ compteId: c.id, soldeInitial: parsed });
        }
      }
      toast.success('Soldes initiaux enregistrés');
      onClose();
    } catch (e) {
      toast.error(`Erreur : ${e instanceof Error ? e.message : 'inconnu'}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-card w-full max-w-md space-y-4 rounded-t-xl p-5 sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2 className="text-foreground text-lg font-semibold">Soldes initiaux</h2>
          <p className="text-muted-foreground text-sm">
            Solde de départ de chaque compte au lancement de l&apos;app. Les mouvements saisis dans
            ECR s&apos;ajoutent par-dessus.
          </p>
        </div>
        <div className="space-y-3">
          {comptes.map((c) => (
            <div key={c.id} className="space-y-1.5">
              <Label>{c.label}</Label>
              <Input
                type="text"
                inputMode="decimal"
                value={values[c.id] ?? ''}
                onChange={(e) => setValues((v) => ({ ...v, [c.id]: e.target.value }))}
                placeholder="0"
                className="font-mono"
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button type="button" disabled={saving} onClick={() => void onSubmit()} className="gap-2">
            {saving && <Loader2 className="size-4 animate-spin" />}
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
}
